import {
  FeeEstimationFailedError,
  InvalidParamError,
  SimulationFailedError,
  getDefaultClient,
  getNetworksData,
} from '@dynamic-labs-sdk/client';
import { createApiClient, getCore } from '@dynamic-labs-sdk/client/core';
import type {
  EvmSimulationResult,
  EvmWalletAccount,
} from '@dynamic-labs-sdk/evm';
import { isEvmWalletAccount } from '@dynamic-labs-sdk/evm';
import type { UserOperation } from 'viem/account-abstraction';

import { canSponsorUserOperation } from '../canSponsorUserOperation';
import { isZerodevWalletAccount } from '../isZerodevWalletAccount';
import { calculateFeeForUserOperation } from '../utils/calculateFeeForUserOperation';
import { hasRequiredGasFields } from '../utils/hasRequiredGasFields';

type SimulateZerodevUserOperationParams = {
  entryPoint: string;
  includeFees?: boolean;
  networkId: string;
  userOperation: Partial<UserOperation>;
  walletAccount: EvmWalletAccount;
};

/**
 * Simulates a ZeroDev user operation to preview its effects before execution.
 *
 * This function uses the Blockaid API to analyze the user operation and returns
 * information about asset transfers, security validation, and optionally fee estimates.
 * For account abstraction wallets, it also checks if gas sponsorship is available.
 *
 * **Important**: A successful simulation does not guarantee the user operation will succeed.
 * The simulation may succeed but indicate the operation would fail (e.g., due to
 * insufficient balance, reverted contract call). Check the `validation` field in the
 * result for potential issues.
 *
 * @param params.walletAccount - The EVM smart wallet account that will execute the operation.
 * @param params.userOperation - The user operation to simulate.
 * @param params.networkId - The network ID where the operation will be executed (e.g., '137' for Polygon).
 * @param params.entryPoint - The entry point contract address for the user operation.
 * @param params.includeFees - Whether to calculate transaction fees (default: false).
 * @param [client] - The Dynamic client instance. Only required when using multiple Dynamic clients.
 * @returns Simulation result with asset diffs, security validation, and optional fee data.
 * @throws {InvalidParamError} If wallet account is not an EVM ZeroDev account or network ID is not configured.
 * @throws {SimulationFailedError} If the simulation API call fails.
 * @throws {FeeEstimationFailedError} If includeFees is true and fee calculation fails.
 */
export const simulateZerodevUserOperation = async (
  {
    walletAccount,
    userOperation,
    networkId,
    entryPoint,
    includeFees = false,
  }: SimulateZerodevUserOperationParams,
  client = getDefaultClient()
): Promise<EvmSimulationResult> => {
  // Validate wallet account type
  if (!isEvmWalletAccount(walletAccount)) {
    throw new InvalidParamError('Wallet account must be an EVM wallet account');
  }

  if (!isZerodevWalletAccount(walletAccount)) {
    throw new InvalidParamError(
      'Wallet account must be a ZeroDev wallet account'
    );
  }

  // Get network data for the specified network
  const networksData = getNetworksData(client);
  const networkData = networksData.find(
    (data) => data.networkId === networkId && data.chain === 'EVM'
  );

  if (!networkData) {
    throw new InvalidParamError(
      `Network with ID ${networkId} is not configured in project settings`
    );
  }

  try {
    // Create API client and call simulation endpoint
    const core = getCore(client);
    const apiClient = createApiClient({}, client);

    // Note: The API endpoint for user operations might be different
    // This implementation assumes simulateUserOp exists on the API client
    // If it doesn't exist, we may need to use simulateEVMTransaction instead
    const response = await apiClient.simulateUserOp({
      environmentId: core.environmentId,
      simulateUserOpRequest: {
        chainId: networkId,
        entryPoint,
        operation: {
          callData: userOperation.callData || '0x',
          callGasLimit: userOperation.callGasLimit?.toString() || '0',
          maxFeePerGas: userOperation.maxFeePerGas?.toString() || '0',
          maxPriorityFeePerGas:
            userOperation.maxPriorityFeePerGas?.toString() || '0',
          nonce: userOperation.nonce?.toString() || '0',
          preVerificationGas:
            userOperation.preVerificationGas?.toString() || '0',
          sender: userOperation.sender || '',
          signature: userOperation.signature || '0x',
          verificationGasLimit:
            userOperation.verificationGasLimit?.toString() || '0',
        },
        value: '0',
      },
    });

    // Calculate fees if requested
    let feeData: EvmSimulationResult['feeData'];

    if (includeFees) {
      // Check if the transaction can be sponsored
      const isSponsored =
        userOperation.callData &&
        userOperation.sender &&
        (await canSponsorUserOperation({
          calls: [
            {
              data: userOperation.callData,
              to: userOperation.sender as `0x${string}`,
              value: 0n,
            },
          ],
          walletAccount,
        }));

      if (isSponsored) {
        // Sponsored transaction - no fee for user
        feeData = {
          gasEstimate: 0n,
          humanReadableAmount: '0',
          nativeAmount: 0n,
          usdAmount: '0.00',
        };
      } else if (hasRequiredGasFields(userOperation)) {
        // User pays fee - calculate it
        feeData = calculateFeeForUserOperation({
          nativeTokenPriceUsd: response.priceData?.nativeTokenUsdPrice,
          networkData,
          userOperationData: {
            callGasLimit: userOperation.callGasLimit,
            maxFeePerGas: userOperation.maxFeePerGas,
            preVerificationGas: userOperation.preVerificationGas,
            verificationGasLimit: userOperation.verificationGasLimit,
          },
        });
      }
    }

    // Map API response to simulation result
    return {
      counterparties: response.counterparties,
      feeData,
      inAssets: response.inAssets || [],
      outAssets: response.outAssets || [],
      priceData: response.priceData,
      showTotalFiat: response.showTotalFiat,
      validation: response.validation
        ? {
            description: response.validation.description,
            reason: response.validation.reason,
            result: response.validation.result as
              | 'benign'
              | 'warning'
              | 'malicious',
          }
        : undefined,
    };
  } catch (error) {
    // Re-throw FeeEstimationFailedError as-is
    if (error instanceof FeeEstimationFailedError) {
      throw error;
    }

    throw new SimulationFailedError({
      cause: error instanceof Error ? error : undefined,
      message: 'Failed to simulate ZeroDev user operation',
    });
  }
};
